import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Calculator, Download, Share2, BookOpen, Trophy, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const QuelStatutSimulator = () => {
  const [inputs, setInputs] = useState({
    caPrevu: '70000',
    charges: '10000',
    situationFamiliale: '1',
    protectionSociale: 'standard',
    objectif: 'revenu'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const ca = parseFloat(inputs.caPrevu) || 0;
    const charges = parseFloat(inputs.charges) || 0;
    const parts = parseFloat(inputs.situationFamiliale) || 1;
    const benefice = ca - charges;

    // Micro-entreprise (BNC)
    const micro_cotisations = ca * 0.256;
    const micro_abattement = ca * 0.34;
    const micro_imposable = ca - micro_abattement;
    let micro_ir = 0;
    const mq = micro_imposable / parts;
    if (mq > 29579) micro_ir += (Math.min(mq, 84577) - 29579) * 0.30 * parts;
    if (mq > 11600) micro_ir += (Math.min(mq, 29579) - 11600) * 0.11 * parts;
    const micro_net = ca - micro_cotisations - micro_ir;
    const micro_protection = 30;

    // SASU à l'IS
    const sasu_salaire_brut = benefice * 0.45;
    const sasu_charges_pat = sasu_salaire_brut * 0.42;
    const sasu_charges_sal = sasu_salaire_brut * 0.22;
    const sasu_salaire_net = sasu_salaire_brut - sasu_charges_sal;
    const sasu_cout_salaire = sasu_salaire_brut + sasu_charges_pat;
    const sasu_resultat = benefice - sasu_cout_salaire;
    let sasu_is = 0;
    if (sasu_resultat > 0) {
      sasu_is = Math.min(sasu_resultat, 42500) * 0.15 + Math.max(sasu_resultat - 42500, 0) * 0.25;
    }
    const sasu_dividendes = Math.max(0, sasu_resultat - sasu_is);
    const sasu_dividendes_nets = sasu_dividendes * 0.70;
    const sasu_net = sasu_salaire_net + sasu_dividendes_nets;
    const sasu_protection = 90;

    // EURL à l'IS
    const eurl_remuneration = benefice * 0.50;
    const eurl_cotisations = eurl_remuneration * 0.45;
    const eurl_net_remuneration = eurl_remuneration - eurl_cotisations;
    const eurl_resultat = benefice - eurl_remuneration - eurl_cotisations;
    let eurl_is = 0;
    if (eurl_resultat > 0) {
      eurl_is = Math.min(eurl_resultat, 42500) * 0.15 + Math.max(eurl_resultat - 42500, 0) * 0.25;
    }
    const eurl_dividendes = Math.max(0, eurl_resultat - eurl_is);
    const eurl_dividendes_nets = eurl_dividendes * 0.70;
    const eurl_net = eurl_net_remuneration + eurl_dividendes_nets;
    const eurl_protection = 60;

    const statuts = [
      { nom: 'Micro-entreprise', net: Math.round(micro_net), protection: micro_protection, simplicite: 95 },
      { nom: 'SASU', net: Math.round(sasu_net), protection: sasu_protection, simplicite: 40 },
      { nom: 'EURL', net: Math.round(eurl_net), protection: eurl_protection, simplicite: 50 },
    ];

    // Score composite selon objectif
    statuts.forEach(s => {
      if (inputs.objectif === 'revenu') {
        s.score = (s.net / Math.max(...statuts.map(x => x.net))) * 70 + s.protection * 0.15 + s.simplicite * 0.15;
      } else if (inputs.objectif === 'protection') {
        s.score = s.protection * 0.50 + (s.net / Math.max(...statuts.map(x => x.net))) * 35 + s.simplicite * 0.15;
      } else {
        s.score = s.simplicite * 0.50 + (s.net / Math.max(...statuts.map(x => x.net))) * 35 + s.protection * 0.15;
      }
    });

    statuts.sort((a, b) => b.score - a.score);

    setResults({ statuts, ca, benefice: Math.round(benefice) });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Quel statut choisir - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Quel statut choisir ?', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`CA prévu : ${parseInt(inputs.caPrevu).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Objectif : ${inputs.objectif}`, 15, y); y += 15;
    if (results.statuts) {
      results.statuts.forEach((s, i) => {
        doc.text(`${i + 1}. ${s.nom} - Net: ${s.net?.toLocaleString()} EUR (score: ${s.score?.toFixed(0)})`, 15, y);
        y += 7;
      });
    }
    y += 10;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, CGI, Code de la Sécurité Sociale', 15, y);
    doc.save('simulation-quel-statut-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Quel statut choisir ?
            </CardTitle>
            <CardDescription>Trouvez le statut le plus adapté à votre situation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>CA annuel prévu (€)</Label>
              <Input data-testid="ca-input" type="text" inputMode="numeric" value={inputs.caPrevu}
                onChange={(e) => handleInputChange('caPrevu', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="70000" className="mt-1" />
            </div>
            <div>
              <Label>Charges / frais annuels (€)</Label>
              <Input data-testid="charges-input" type="text" inputMode="numeric" value={inputs.charges}
                onChange={(e) => handleInputChange('charges', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="10000" className="mt-1" />
            </div>
            <div>
              <Label>Parts fiscales</Label>
              <Input data-testid="parts-input" type="text" inputMode="decimal" value={inputs.situationFamiliale}
                onChange={(e) => handleInputChange('situationFamiliale', e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="1" className="mt-1" />
            </div>
            <div>
              <Label>Votre priorité</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[['revenu', 'Max revenu'], ['protection', 'Protection'], ['simplicite', 'Simplicité']].map(([key, label]) => (
                  <Button key={key} size="sm" data-testid={`objectif-${key}-btn`}
                    variant={inputs.objectif === key ? 'default' : 'outline'}
                    className={inputs.objectif === key ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    onClick={() => handleInputChange('objectif', key)}>{label}</Button>
                ))}
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>URSSAF 2026 - Taux micro-entreprise BNC 25.6%</li>
                    <li>SASU : charges ~42% patronales, IS 15%/25%</li>
                    <li>EURL TNS : cotisations ~45%, IS 15%/25%</li>
                    <li>Barème IR 2026 - Loi de finances 2026</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" /> Classement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {results.statuts?.map((s, i) => (
              <div key={s.nom} className={`p-4 rounded-lg border-2 ${i === 0 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-black ${i === 0 ? 'text-green-700' : 'text-gray-500'}`}>#{i + 1}</span>
                    <span className="font-bold text-lg">{s.nom}</span>
                    {i === 0 && <Badge className="bg-green-100 text-green-800">Recommandé</Badge>}
                  </div>
                  <span className="text-xs text-gray-500">Score : {s.score?.toFixed(0)}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Revenu net</p>
                    <p className="text-lg font-bold text-green-900" data-testid={`result-net-${i}`}>{s.net?.toLocaleString()}€</p>
                    <p className="text-xs text-gray-400">{Math.round(s.net / 12)?.toLocaleString()}€/mois</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Protection</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${s.protection}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{s.protection}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Simplicité</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${s.simplicite}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{s.simplicite}%</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="grid grid-cols-3 gap-3 mt-4">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026, CGI, Code Sécurité Sociale</p>
              <p>Simulation indicative. Consultez un expert-comptable.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuelStatutSimulator;
